import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useKaannos } from '../lib/i18n';

/**
 * Modaali. Toteutettu selaimen `<dialog>`-elementillä, joten Esc, taustan
 * inertointi ja fokuksen pitäminen dialogin sisällä tulevat selaimelta
 * eivätkä omasta koodista — käsin kirjoitettu fokusansa on tavallinen
 * paikka, jossa saavutettavuus hajoaa hiljaa.
 */
export default function Dialogi({
  otsikko,
  auki,
  onSulje,
  children,
  leveys = 'max-w-lg',
}: {
  otsikko: string;
  auki: boolean;
  onSulje: () => void;
  children: ReactNode;
  leveys?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const otsikkoId = useId();
  const { t } = useKaannos();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (auki && !dialog.open) dialog.showModal();
    if (!auki && dialog.open) dialog.close();
  }, [auki]);

  return (
    <dialog
      ref={ref}
      // Ilman tätä dialogi on ruudunlukijalle nimetön: otsikko on sen
      // sisällä, mutta sisältö ei nimeä säiliötä. Nimetön modaali
      // ilmoitetaan pelkkänä ”valintaikkuna”, eikä kuulija tiedä mikä
      // aukesi.
      aria-labelledby={otsikkoId}
      onCancel={(event) => {
        event.preventDefault();
        onSulje();
      }}
      onClick={(event) => {
        // Klikkaus taustaan sulkee. Kohde on dialogi itse vain silloin,
        // kun osuma tuli sen ulkopuolelle — sisältö on oma elementtinsä.
        if (event.target === ref.current) onSulje();
      }}
      className={`bg-pinta-korotettu text-teksti border-reuna m-auto w-full rounded-xl border p-0 shadow-xl backdrop:bg-black/40 ${leveys}`}
    >
      <div className="border-reuna flex items-center justify-between border-b px-5 py-3">
        <h2 id={otsikkoId} className="text-teksti text-base font-semibold tracking-tight">
          {otsikko}
        </h2>
        <button
          type="button"
          onClick={onSulje}
          aria-label={t('yleinen.sulje')}
          className="text-teksti-vaimea hover:text-teksti rounded-md p-1"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
      <div className="px-5 py-4">{children}</div>
    </dialog>
  );
}
